import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { Auth } from 'aws-amplify';
import { NotificationContext } from 'common/NotificationContainer/NotificationContainer';
import { useLazyGetCall } from 'common/api/useApiCall';
import Cache from 'common/cache/lib/utils/Cache';
import { URLS } from 'constants/URL';
import { REFRESH_TOKEN_AFTER } from 'constants/appConstants';
import useInterval from 'helpers/useInterval';
import { adaptUserPermissionResponse } from './adaptUserPrivilegeResponse';

interface UserLoginInfoProps {
  children: any;
}

export const UserLoginInfoContext = createContext({});
const SetUserLoginInfoContext = createContext((user: any) => {});
export const UserPermissionsContext = createContext<{
  hasPermission: (_permission: string) => boolean;
  isPermissionsLoading: boolean;
  getUserPrivileges: any;
}>(null);

export const useUserLoginInfoContext = (): any => {
  const userLoginInfo = useContext(UserLoginInfoContext);

  return Object.keys(userLoginInfo)?.length > 0
    ? userLoginInfo
    : JSON.parse(Cache.get('loginInfo') || '{}');
};
export const useSetUserLoginInfoContext = () => {
  const setUserLoginInfo = useContext(SetUserLoginInfoContext);
  return setUserLoginInfo;
};

const isUnrecoverableAuthError = (e: any): boolean =>
  ['NotAuthorizedException', 'UserNotFoundException'].includes(
    e?.code || e?.name,
  );

const refreshSessionTokens = async (onFailure: () => void) => {
  try {
    const cognitoUser = await Auth.currentAuthenticatedUser();
    const currentSession = await Auth.currentSession();
    cognitoUser.refreshSession(
      currentSession.getRefreshToken(),
      (err, session) => {
        if (err || !session) {
          console.log('Unable to refresh Token', err);
          if (isUnrecoverableAuthError(err)) {
            onFailure();
          }
          return;
        }
        const { idToken, accessToken } = session;
        Cache.set('idToken', idToken?.jwtToken);
        Cache.set('accessToken', accessToken?.jwtToken);
      },
    );
  } catch (e) {
    console.log('Unable to refresh Token', e);
    if (isUnrecoverableAuthError(e)) {
      onFailure();
    }
  }
};

export const UserLoginInfo = (props: UserLoginInfoProps) => {
  const { children } = props;
  const [loginInfo, setLoginInfo] = useState({});
  const { setRefreshCount } = useContext(NotificationContext);

  const {
    response: userPermissions,
    isLoading: userPermissionsLoading,
    postData: getUserPermissions,
  } = useLazyGetCall(URLS.getUserPermissions, adaptUserPermissionResponse);

  useEffect(() => {
    if (Cache.get('idToken')) {
      getUserPermissions();
    }
  }, [loginInfo]);

  const setUserLoginInfo = useCallback(
    (newLoginInfo?: any) => {
      setLoginInfo(newLoginInfo);
      Cache.set('loginInfo', JSON.stringify(newLoginInfo));
      Cache.set('userName', newLoginInfo?.username);
      if (newLoginInfo?.signInUserSession?.idToken?.jwtToken) {
        Cache.set(
          'idToken',
          newLoginInfo?.signInUserSession?.idToken?.jwtToken,
        );
        Cache.set(
          'accessToken',
          newLoginInfo?.signInUserSession?.accessToken?.jwtToken,
        );
        if (Cache.get('idToken') && Cache.get('userName') !== 'null') {
          getUserPermissions();
        }
      }
      setRefreshCount(1);
    },
    [getUserPermissions, setRefreshCount],
  );

  const clearExpiredSession = useCallback(() => {
    Auth.signOut();
    setLoginInfo({});
    Cache.clear();
  }, []);

  useInterval(() => {
    if (!Cache.get('accessToken') && !Cache.get('idToken')) {
      return;
    }
    refreshSessionTokens(clearExpiredSession);
  }, REFRESH_TOKEN_AFTER);

  const hasPermission = (_permission) => {
    return userPermissions?.includes(_permission);
  };

  return (
    <UserPermissionsContext.Provider
      value={{
        hasPermission,
        isPermissionsLoading: userPermissionsLoading,
        getUserPrivileges: getUserPermissions,
      }}
    >
      <SetUserLoginInfoContext.Provider value={setUserLoginInfo}>
        <UserLoginInfoContext.Provider value={loginInfo}>
          {children}
        </UserLoginInfoContext.Provider>
      </SetUserLoginInfoContext.Provider>
    </UserPermissionsContext.Provider>
  );
};
